import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Download, FileText, Heart, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API, api, downloadFile, errMsg } from "@/lib/api";
import { LaunchPlanner } from "@/components/LaunchPlanner";
import { Markdown, PageHeader } from "@/components/Bits";
import { FillTemplate } from "@/components/FillTemplate";

export const embedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const loom = url.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

export const ResourceModal = ({ item, onClose }) => {
  if (!item) return null;
  const embed = embedUrl(item.video_url);
  const copy = () => navigator.clipboard.writeText(item.content).then(() => toast.success("Copied to clipboard"));
  const download = () => downloadFile(`/resources/${item.id}/file`, item.file_name).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[85vh] overflow-y-auto bg-card border-border [&>*]:min-w-0" data-testid="resource-modal">
        <DialogHeader>
          <div className="eyebrow">{item.category} · {item.type}</div>
          <DialogTitle className="font-serif text-2xl">{item.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{item.description}</p>
        {item.thumb_path && <img src={`${API}/resources/${item.id}/thumb`} alt={`Preview of ${item.title}`} data-testid="resource-preview-large" className="w-full max-h-[60vh] object-contain rounded-xl border border-border bg-secondary" />}
        {embed && <div className="aspect-video rounded-xl overflow-hidden border border-border"><iframe src={embed} title={item.title} className="w-full h-full" allowFullScreen /></div>}
        {item.type === "video" && !embed && !item.video_url && <p className="text-sm text-muted-foreground italic">Video coming soon.</p>}
        {item.video_url && !embed && <a href={item.video_url} target="_blank" rel="noreferrer" className="text-pinkb underline text-sm">Watch video</a>}
        {item.content && <div className="min-w-0 rounded-xl border border-border bg-secondary p-5"><Markdown testId="resource-content">{item.content}</Markdown></div>}
      <FillTemplate item={item} />
        <div className="flex flex-wrap gap-3">
          {item.content && <Button variant="outline" onClick={copy} data-testid="resource-copy-btn" className="rounded-full"><Copy className="h-4 w-4 mr-2" />Copy text</Button>}
          {item.file_path && <Button onClick={download} data-testid="resource-download-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white"><Download className="h-4 w-4 mr-2" />Download {item.file_name}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Checklist = ({ p, onDone }) => {
  if (!p?.checklist?.length) return null;
  const toggle = async (i) => {
    try {
      const { data } = await api.post(`/resources/seasonal/${p.key}/check`, { index: i, done: !p.done.includes(i) });
      onDone(data.done);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pct = Math.round((p.done.length / p.checklist.length) * 100);
  return (
    <div className="mt-5 rounded-xl bg-white text-navy p-5" data-testid={`checklist-${p.key}`}>
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="font-serif text-lg">Quick wins: {p.name}</div>
        <div className="text-xs font-mono" data-testid="checklist-progress">{p.done.length}/{p.checklist.length} done</div>
      </div>
      <div className="h-1.5 rounded-full bg-secondary mb-4 overflow-hidden"><div className="h-full bg-leaf transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <ul className="grid sm:grid-cols-2 gap-2">
        {p.checklist.map((item, i) => (
          <li key={item}>
            <button onClick={() => toggle(i)} data-testid={`checklist-item-${i}`} className="w-full flex items-start gap-2 text-left text-sm rounded-lg px-2 py-1.5 hover:bg-secondary transition-colors">
              <span className={`mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center ${p.done.includes(i) ? "bg-leaf border-leaf text-white" : "border-navy/30"}`}>{p.done.includes(i) && <Check className="h-3 w-3" />}</span>
              <span className={p.done.includes(i) ? "line-through text-muted-foreground" : ""}>{item}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function Library({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/resources").then((r) => {
      setItems(r.data);
      const qp = new URLSearchParams(window.location.search);
      const hit = r.data.find((x) => x.id === qp.get("open")) || (qp.get("q") && r.data.find((x) => x.title.toLowerCase().includes(qp.get("q").toLowerCase())));
      if (hit) { setOpen(hit); api.post(`/resources/${hit.id}/view`).catch(() => {}); }
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  const cats = useMemo(() => ["All", ...new Set(items.map((i) => i.category))], [items]);
  const [packs, setPacks] = useState([]);
  const [pack, setPack] = useState(() => new URLSearchParams(window.location.search).get("pack"));
  useEffect(() => { api.get("/resources/seasonal").then((r) => setPacks(r.data)).catch(() => {}); }, []);
  const shown = items.filter((i) => (pack ? i.pack === pack : (cat === "All" || i.category === cat) && (type === "all" || (type === "favourites" ? i.favourite : i.type === type))));

  const openItem = (it) => {
    setOpen(it);
    api.post(`/resources/${it.id}/view`).catch(() => {});
  };
  const toggleFav = async (e, it) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/resources/${it.id}/favourite`);
      setItems((l) => l.map((x) => (x.id === it.id ? { ...x, favourite: data.favourite } : x)));
      toast.success(data.favourite ? "Saved to favourites" : "Removed from favourites");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const filters = (
    <div className="flex flex-wrap gap-2">
      {["all", "template", "video", "favourites"].map((t) => (
        <button key={t} onClick={() => setType(t)} data-testid={`library-type-${t}`}
          className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors ${type === t ? "bg-navy text-white border-navy" : "border-border text-muted-foreground hover:text-foreground"}`}>
          {t === "all" ? "All" : t === "favourites" ? "Favourites" : `${t}s`}
        </button>
      ))}
    </div>
  );
  return (
    <div>
      {embedded ? <div className="mb-6">{filters}</div> : <PageHeader eyebrow="Library" title="Templates & videos">{filters}</PageHeader>}
      {packs.length > 0 && (
        <div className="mb-8 rounded-2xl bg-navy text-white p-6" data-testid="seasonal-packs">
          <div className="eyebrow !text-sun mb-3">Planning packs · launches and seasons coming up</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {packs.map((p, i) => (
              <button key={p.key} onClick={() => setPack(pack === p.key ? null : p.key)} data-testid={`pack-${p.key}`}
                className={`text-left rounded-xl p-4 border transition-colors ${pack === p.key ? "bg-pinkb border-pinkb" : i === 0 ? "bg-white/15 border-white/30" : "bg-white/5 border-white/10 hover:bg-white/10"}`}>
                <div className="font-serif text-lg leading-tight">{p.name}</div>
                <div className="text-xs text-white/70 mt-1">{p.evergreen ? (p.date ? `${new Date(p.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · ${p.days_until >= 0 ? `${p.days_until} days to go` : "launched"}` : "Any time · set your launch date") : `${new Date(p.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · ${p.days_until} days`}</div>
                <div className={`text-[10px] font-mono uppercase mt-2 ${p.planning_now ? "text-sun" : "text-white/50"}`}>
                  {p.evergreen ? "New opening or launch" : p.planning_now ? "Plan now" : `Plan from ${new Date(p.plan_from).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`} · {p.resource_ids.length} templates
                </div>
              </button>
            ))}
          </div>
          {pack === "launch" && <LaunchPlanner p={packs.find((x) => x.key === "launch")} onChange={(np) => setPacks((l) => l.map((x) => (x.key === "launch" ? { ...x, ...np, done: np.cycle === x.cycle ? x.done : [] } : x)))} />}
          {pack && <Checklist p={packs.find((x) => x.key === pack)} onDone={(done) => setPacks((l) => l.map((x) => (x.key === pack ? { ...x, done } : x)))} />}
          {pack && <button onClick={() => setPack(null)} className="mt-3 text-xs text-white/70 hover:text-white underline" data-testid="pack-clear">Show all templates</button>}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-8">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`library-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${cat === c ? "border-pinkb text-pinkb bg-pinkb/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {c}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {shown.map((it, i) => (
          <div key={it.id} role="button" tabIndex={0} onClick={() => openItem(it)} onKeyDown={(e) => e.key === "Enter" && openItem(it)} data-testid={`resource-card-${it.id}`} style={{ animationDelay: `${i * 60}ms` }}
            className="fade-up cursor-pointer text-left rounded-2xl border border-border bg-card p-6 hover:bg-secondary hover:border-pinkb/50 transition-colors duration-300 group">
            {it.thumb_path && (
              <div className="-mx-6 -mt-6 mb-5 aspect-[16/9] overflow-hidden rounded-t-2xl border-b border-border bg-secondary">
                <img src={`${API}/resources/${it.id}/thumb`} alt={`Preview of ${it.title}`} loading="lazy" data-testid={`resource-thumb-${it.id}`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
              </div>
            )}
            <div className="flex justify-between">
              <span className="flex items-center gap-2">{it.type === "video" ? <PlayCircle className="h-6 w-6 text-rose-600" /> : <FileText className="h-6 w-6 text-pinkb" />}
                {it.file_name && <span className="text-[10px] font-mono uppercase rounded bg-secondary px-1.5 py-0.5 text-muted-foreground" data-testid={`resource-filetype-${it.id}`}>{it.file_name.split(".").pop()}</span>}</span>
              <button onClick={(e) => toggleFav(e, it)} data-testid={`resource-fav-${it.id}`} aria-label="Toggle favourite" className="p-1 -m-1">
                <Heart className={`h-5 w-5 transition-colors ${it.favourite ? "fill-pinkb text-pinkb" : "text-muted-foreground hover:text-pinkb"}`} />
              </button>
            </div>
            <div className="mt-6 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{it.category}</div>
            <h3 className="mt-1 text-lg font-semibold leading-snug">{it.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{it.description}</p>
          </div>
        ))}
        {shown.length === 0 && <p className="text-muted-foreground text-sm" data-testid="library-empty">{type === "favourites" ? "No favourites yet. Tap the heart on any template to save it here." : "No resources here yet."}</p>}
      </div>
      <ResourceModal item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
